import gql from "graphql-tag";

import {setAnalyticsCookies} from "@khan/auth-shared";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {cleverCreateAccountWithEmailV2MutationType} from "./__graphql-types__/index.ts";

export const CLEVER_SIGNUP_MUTATION_V2: GqlOperation<
    cleverCreateAccountWithEmailV2MutationType["response"],
    cleverCreateAccountWithEmailV2MutationType["variables"]
> = gqlOp(gql`
    mutation cleverCreateAccountWithEmailV2Mutation(
        $transferToken: String!
        $email: String!
        $birthdate: Date
    ) {
        cleverCreateAccountWithEmailV2(
            transferToken: $transferToken
            email: $email
            birthdate: $birthdate
        ) {
            error {
                code
            }
            user {
                id
                kaid
            }
        }
    }
`);

/**
 * Creates a new account for a Clever user
 *
 * @param {Variables} variables The variables that will be sent to the mutation.
 * @param {string} variables.transferToken The transfer token, that will be used
 * will be exchanged for an auth token so we can fetch the users information
 * @param {string} variables.email The email address that will be used when we create
 * an account
 * @param {date} variables.birthdate The birthdate for the new user.
 *
 * @returns {Promise<CleverSignUpResult>} The mutation result.
 */
export const signUpWithClever = async (
    gqlClient: ApolloGqlClient,
    variables: cleverCreateAccountWithEmailV2MutationType["variables"],
) => {
    try {
        const result = await gqlClient.mutate(CLEVER_SIGNUP_MUTATION_V2, {
            variables,
        });

        const data = result.data?.cleverCreateAccountWithEmailV2;
        if (data?.error) {
            return {
                error: data.error.code,
            };
        }

        setAnalyticsCookies(true, data?.user?.kaid || "");

        return {
            error: null,
        };
    } catch (error) {
        // Throw a generic error.
        throw new KAError("Failed to create account ", Errors.Internal);
    }
};
